import { theme, card, type as text } from "../../theme";
import { RulerProgress } from "../../components/Drawing";
import { PortalLink } from "../../components/PortalLink";
import { tableHeadRow, fmtDay, secondaryBtn } from "./shared";

// Training insights (GET /resilience/training/insights, rules in backend/src/lib/trainingInsights.ts):
// awareness coverage by department and site, the phishing simulation trend, and the members to chase.

export interface CoverageRow { id: string | null; name: string; members: number; covered: number; pct: number | null }
export interface PhishingMonth { month: string; simulations: number; clicked: number; reported: number; clickRate: number | null; reportRate: number | null }
export type OverdueReason = "no_awareness" | "awareness_expired" | "assignment_overdue";
export interface OverdueMember {
  userId: string;
  name: string;
  email: string | null;
  reasons: OverdueReason[];
  awarenessCompletedAt: string | null;
  awarenessExpiresAt: string | null;
  assignments: Array<{ assignmentId: string; title: string; dueAt: string }>;
}
export interface TrainingInsightsView {
  total: number;
  covered: number;
  pct: number | null;
  byDepartment: CoverageRow[];
  bySite: CoverageRow[];
  phishingTrend: PhishingMonth[];
  overdue: OverdueMember[];
}

export const REASON_LABELS: Record<OverdueReason, string> = {
  no_awareness: "No awareness training recorded",
  awareness_expired: "Awareness training out of date",
  assignment_overdue: "Assigned training overdue",
};

function CoverageTable({ title, rows, empty }: { title: string; rows: CoverageRow[]; empty: string }) {
  return (
    <div style={{ ...card, padding: 0, overflowX: "auto" }}>
      <div style={{ ...text.sectionTitle, margin: 0, padding: "12px 16px", borderBottom: "1px solid var(--c-border)" }}>{title}</div>
      {rows.length === 0 ? <div style={{ ...text.body, padding: 16, color: theme.color.muted }}>{empty}</div> : (
        <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={tableHeadRow}>
            {["Name", "Members", "In date", "Coverage"].map((h) => <th key={h} style={{ padding: "8px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id ?? "none"} style={{ borderBottom: "1px solid var(--c-border)" }}>
                <td style={{ padding: "8px 14px", color: r.id ? theme.color.text : theme.color.muted, fontWeight: r.id ? 600 : 400 }}>{r.name}</td>
                <td style={{ padding: "8px 14px", color: theme.color.muted }}>{r.members}</td>
                <td style={{ padding: "8px 14px", color: theme.color.muted }}>{r.covered}</td>
                <td style={{ padding: "8px 14px", minWidth: 180 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <RulerProgress value={r.pct ?? 0} fill={r.pct == null ? theme.color.borderStrong : r.pct >= 90 ? theme.color.success : r.pct >= 70 ? theme.color.warning : theme.color.danger} label={`${r.pct ?? 0}% in date`} height={10} style={{ flex: 1 }} />
                    <span style={{ ...text.hint, fontWeight: 600, color: theme.color.text, minWidth: 36, textAlign: "right" }}>{r.pct != null ? `${r.pct}%` : "—"}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (m: string) => MONTHS[Number(m.slice(5, 7)) - 1] ?? m;

/** Click rate and report rate per month as paired bars; a month without a simulation is marked, not zero. */
export function PhishingTrendChart({ months }: { months: PhishingMonth[] }) {
  const W = 640, H = 200, padL = 34, padB = 26, padT = 10;
  const plotH = H - padB - padT;
  const step = (W - padL - 8) / Math.max(1, months.length);
  const barW = Math.max(4, Math.min(14, step / 3));
  const y = (pct: number) => padT + plotH - (pct / 100) * plotH;
  const any = months.some((m) => m.simulations > 0);
  return (
    <div style={{ ...card, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 6 }}>
        <span style={{ ...text.sectionTitle, margin: 0 }}>Phishing simulations, last 12 months</span>
        <span style={{ ...text.hint, display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: theme.color.danger }} />Clicked</span>
        <span style={{ ...text.hint, display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: theme.color.success }} />Reported</span>
      </div>
      {!any ? <div style={{ ...text.body, color: theme.color.muted }}>No phishing simulation results in the last twelve months. Record them as training records of type "Phishing simulation" with the result clicked or reported.</div> : (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Phishing click rate and report rate by month" style={{ display: "block", fontFamily: theme.font }}>
          {[0, 25, 50, 75, 100].map((t) => (
            <g key={t}>
              <line x1={padL} x2={W - 4} y1={y(t)} y2={y(t)} stroke="var(--c-border)" strokeWidth={1} />
              <text x={padL - 6} y={y(t) + 3} textAnchor="end" fontSize={10} fill="var(--c-subtle)">{t}%</text>
            </g>
          ))}
          {months.map((m, i) => {
            const cx = padL + step * i + step / 2;
            return (
              <g key={m.month}>
                <title>{`${m.month}: ${m.simulations} result${m.simulations === 1 ? "" : "s"}${m.simulations ? `, ${m.clickRate}% clicked, ${m.reportRate}% reported` : ""}`}</title>
                {m.simulations === 0
                  ? <circle cx={cx} cy={y(0) - 3} r={2} fill="var(--c-faint)" />
                  : <>
                    <rect x={cx - barW - 1} y={y(m.clickRate ?? 0)} width={barW} height={Math.max(1, y(0) - y(m.clickRate ?? 0))} rx={2} fill="var(--c-danger)" />
                    <rect x={cx + 1} y={y(m.reportRate ?? 0)} width={barW} height={Math.max(1, y(0) - y(m.reportRate ?? 0))} rx={2} fill="var(--c-success)" />
                  </>}
                <text x={cx} y={H - 8} textAnchor="middle" fontSize={10} fill="var(--c-subtle)">{monthLabel(m.month)}</text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

export function TrainingBreakdown({ insights }: { insights: TrainingInsightsView }) {
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
        <CoverageTable title="Awareness coverage by department" rows={insights.byDepartment} empty="No active members." />
        <CoverageTable title="Awareness coverage by site" rows={insights.bySite} empty="No active members." />
      </div>
      <PhishingTrendChart months={insights.phishingTrend} />
    </div>
  );
}

/** Members to chase: no in-date awareness training, or an assignment past its due date. */
export function TrainingOverdueList({ rows, canEdit, onRecord }: { rows: OverdueMember[]; canEdit: boolean; onRecord: (userId: string) => void }) {
  if (rows.length === 0) return <div style={{ ...text.body, ...card, padding: 20, color: theme.color.successText }}>Nobody is overdue: every active member has in-date awareness training and no assignment is past due.</div>;
  return (
    <div style={{ ...card, padding: 0, overflowX: "auto" }}>
      <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
        <thead><tr style={tableHeadRow}>
          {["Member", "Why", "Awareness training", "Overdue assignments", ""].map((h, i) => <th key={i} style={{ padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.userId} style={{ borderBottom: "1px solid var(--c-border)", verticalAlign: "top" }}>
              <td style={{ padding: "10px 14px", color: theme.color.text, fontWeight: 600 }}>{o.name}<div style={{ ...text.meta, fontWeight: 400 }}>{o.email}</div></td>
              <td style={{ padding: "10px 14px" }}>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {o.reasons.map((r) => <span key={r} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 2, background: r === "assignment_overdue" || r === "no_awareness" ? theme.color.dangerBg : theme.color.warningBg, color: r === "assignment_overdue" || r === "no_awareness" ? theme.color.dangerText : theme.color.warningText }}>{REASON_LABELS[r]}</span>)}
                </div>
              </td>
              <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap" }}>
                {o.awarenessCompletedAt ? `completed ${fmtDay(o.awarenessCompletedAt)}` : "none recorded"}
                {o.awarenessExpiresAt && <div style={text.meta}>expired {fmtDay(o.awarenessExpiresAt)}</div>}
              </td>
              <td style={{ padding: "10px 14px", color: theme.color.muted }}>
                {o.assignments.length === 0 ? "—" : o.assignments.map((a) => (
                  <div key={a.assignmentId} style={text.hint}>
                    {/* No reminder route exists for a single assignee; the assignment itself is where a writer chases. */}
                    <PortalLink to={`/resilience/training?assignment=${a.assignmentId}`} style={{ color: "var(--c-accentText)", textDecoration: "none" }} title="Open the assignment to chase or excuse">{a.title}</PortalLink>
                    <span style={{ color: theme.color.dangerText }}> due {fmtDay(a.dueAt)}</span>
                  </div>
                ))}
              </td>
              <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                {o.assignments[0] && <PortalLink to={`/resilience/training?assignment=${o.assignments[0].assignmentId}`} style={{ ...secondaryBtn, textDecoration: "none", display: "inline-block" }}>Remind</PortalLink>}{" "}
                {canEdit && o.reasons.some((r) => r !== "assignment_overdue") && <button type="button" style={secondaryBtn} onClick={() => onRecord(o.userId)}>Record training</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
